import {Cell, Row, Worksheet} from 'exceljs';
import {dataTypeString} from 'src/components/data-types/utils/dataTypeString';
import {MAPPED_CHANGE_REQUEST_STATUS} from 'src/modules/constants';
import {
    decorateCell,
    decorateLinkCell,
    decorateSheetTitleCell,
    decorateTableCell,
    decorateTableHeaderCell,
    setCellValue,
} from 'src/modules/downloads/cells/cells';
import {DEFAULT_DATE_FORMAT, HorizontalAlignment, VerticalAlignment} from 'src/modules/downloads/cells/styles';
import {printLogo} from 'src/modules/downloads/commons/print-logo';
import {printMetadata} from 'src/modules/downloads/commons/print-metadata';
import {getUserName} from 'src/modules/downloads/utils/get-user-name';
import {ColumnTypes} from 'src/modules/event/constants';
import {createEventReviewRoutePath} from 'src/modules/event-review/event-review-route-planner';
import {AttributeDetailInfo} from 'src/openapi/models/AttributeDetailInfo';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {EventDetailInfo} from 'src/openapi/models/EventDetailInfo';
import {User} from 'src/openapi/models/User';
import {capitalize} from 'src/utils/capitalize';
import {getPossibleValuesOrDefault} from 'src/utils/getPossibleValuesOrDefault';

import {formatDate} from '@pb/kit-formatters';

const FIRST_ROW = 1;
const FIRST_ROW_HEIGHT = 30;
const TABLE_HEADER_HEIGHT = 35;
const TABLE_HEADER_ROW = 16;
const FIRST_DATA_ROW = 17;
const SHEET_TITLE = 'Event';
const LINK = 'Link:';
const LINK_CAPTION = 'View Event';
const NO_DATA_AVAILABLE = 'No data available';
const FIRST_CHANGE_LOG_ROW = 4;
const CHANGE_LOG_LIMIT = 5;
const CHANGE_LOG = 'Change Log:';
const ALL_CHANGE_LOG = 'View All Change Log';
const EMPTY_CHANGE_LOG_MESSAGE = 'No changes made to this event';
const NO_ATTRIBUTE_USAGE = 'No Usage';

export const printChangeLog = (
    worksheet: Worksheet,
    changeRequests: Array<ChangeRequest>,
    usersInfo: Record<string, User>,
) => {
    decorateCell(worksheet.getCell('D3'), setCellValue(CHANGE_LOG));

    if (!changeRequests.length) {
        decorateCell(worksheet.getCell('D4'), setCellValue(EMPTY_CHANGE_LOG_MESSAGE));
        return;
    }

    const limitedChangeRequests = changeRequests.slice(0, CHANGE_LOG_LIMIT).flat();

    limitedChangeRequests.forEach((changeRequest, index) => {
        const cellIndex = FIRST_CHANGE_LOG_ROW + index;
        const updateDate = formatDate(changeRequest.updateDate, DEFAULT_DATE_FORMAT);
        const status = MAPPED_CHANGE_REQUEST_STATUS[changeRequest.status];
        const authorEmail = changeRequest.authorEmail;
        const userName = getUserName(authorEmail, usersInfo[authorEmail]);
        const logInfo = `${updateDate} ${userName} ${status}`;
        const pathToChangeRequest = createEventReviewRoutePath({changeRequestId: changeRequest.id ?? 0});

        const linkToChangeLog = (cell: Partial<Cell>) =>
            cell.value = {text: logInfo, hyperlink: `${window.location.origin}${pathToChangeRequest}`};
        decorateLinkCell(worksheet.getCell(`D${cellIndex}`), linkToChangeLog);
    });

    if (changeRequests.length > CHANGE_LOG_LIMIT) {
        const linkToEventView = (cell: Partial<Cell>) =>
            cell.value = {text: ALL_CHANGE_LOG, hyperlink: window.location.href};
        decorateLinkCell(worksheet.getCell('D9'), linkToEventView);
    }
};

export const printEventDetailInfo = (worksheet: Worksheet, event: EventDetailInfo) => {
    const deprecated = capitalize(event.deprecated.toString());
    const reported = capitalize(event.reported.toString());
    const decorated = capitalize(event.decorated.toString());
    const families = event.families
        .map(family => family).join(', ');
    const firstUsageDate = formatDate(event.firstUsageDate, DEFAULT_DATE_FORMAT);
    const lastUsageDate = formatDate(event.lastUsageDate, DEFAULT_DATE_FORMAT);
    const trigger = event.trigger ?? NO_DATA_AVAILABLE;

    const setEventDetailInfoKey = (key: string) => (cell: Partial<Cell>) => {
        cell.alignment = {horizontal: HorizontalAlignment.RIGHT, vertical: VerticalAlignment.TOP};
        cell.value = key;
        return cell;
    };

    const setEventDetailInfoValue = (value: string, wrap = false) => (cell: Partial<Cell>) => {
        cell.alignment = {horizontal: HorizontalAlignment.LEFT, vertical: VerticalAlignment.TOP, wrapText: wrap};
        cell.value = value;
        return cell;
    };

    decorateCell(worksheet.getCell('A4'), setEventDetailInfoKey('Event Type:'));
    decorateCell(worksheet.getCell('A5'), setEventDetailInfoKey('Event Code:'));
    decorateCell(worksheet.getCell('A6'), setEventDetailInfoKey('Event Source:'));
    decorateCell(worksheet.getCell('A7'), setEventDetailInfoKey('Reported:'));
    decorateCell(worksheet.getCell('A8'), setEventDetailInfoKey('Mixpanel decorated:'));
    decorateCell(worksheet.getCell('A9'), setEventDetailInfoKey('Deprecated:'));
    decorateCell(worksheet.getCell('A10'), setEventDetailInfoKey('First Usage Date:'));
    decorateCell(worksheet.getCell('A11'), setEventDetailInfoKey('Last Usage Data:'));
    decorateCell(worksheet.getCell('A12'), setEventDetailInfoKey('Families:'));
    decorateCell(worksheet.getCell('A13'), setEventDetailInfoKey('Event Description:'));
    decorateCell(worksheet.getCell('A14'), setEventDetailInfoKey('Trigger:'));

    decorateCell(worksheet.getCell('B4'), setEventDetailInfoValue(event.type));
    decorateCell(worksheet.getCell('B5'), setEventDetailInfoValue(event.code));
    decorateCell(worksheet.getCell('B6'), setEventDetailInfoValue(event.source));
    decorateCell(worksheet.getCell('B7'), setEventDetailInfoValue(reported));
    decorateCell(worksheet.getCell('B8'), setEventDetailInfoValue(decorated));
    decorateCell(worksheet.getCell('B9'), setEventDetailInfoValue(deprecated));
    decorateCell(worksheet.getCell('B10'), setEventDetailInfoValue(firstUsageDate));
    decorateCell(worksheet.getCell('B11'), setEventDetailInfoValue(lastUsageDate));
    decorateCell(worksheet.getCell('B12'), setEventDetailInfoValue(families, true));
    decorateCell(worksheet.getCell('B13'), setEventDetailInfoValue(event.description, true));
    decorateCell(worksheet.getCell('B14'), setEventDetailInfoValue(trigger, true));
};

export const printSheetHeader = (worksheet: Worksheet, userName: string) => {
    const row = worksheet.getRow(FIRST_ROW) as Partial<Row>;
    row.height = FIRST_ROW_HEIGHT;

    const link = (cell: Partial<Cell>) =>
        cell.value = {text: LINK_CAPTION, hyperlink: window.location.href};

    printLogo(worksheet);
    printMetadata(worksheet, userName);
    decorateSheetTitleCell(worksheet.getCell('D1'), setCellValue(SHEET_TITLE, HorizontalAlignment.LEFT));
    decorateCell(worksheet.getCell('A3'), setCellValue(LINK, HorizontalAlignment.RIGHT));
    decorateLinkCell(worksheet.getCell('B3'), link);
};

export const printTableDataHeader = (worksheet: Worksheet) => {
    const row = worksheet.getRow(TABLE_HEADER_ROW) as Partial<Row>;
    row.height = TABLE_HEADER_HEIGHT;

    decorateTableHeaderCell(worksheet.getCell('A16'), 25,
        setCellValue(ColumnTypes.ATTRIBUTE_NAME, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('B16'), 70,
        setCellValue(ColumnTypes.ATTRIBUTE_DESCRIPTION, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('C16'), 25,
        setCellValue(ColumnTypes.DATA_TYPE, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('D16'), 75,
        setCellValue(ColumnTypes.POSSIBLE_VALUES, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('E16'), 15,
        setCellValue(ColumnTypes.REQUIRED, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('F16'), 15,
        setCellValue(ColumnTypes.DEPRECATED, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('G16'), 20,
        setCellValue(ColumnTypes.ATTRIBUTE_LAST_USAGE_DATE, HorizontalAlignment.CENTER));
};

export const printTableData = (worksheet: Worksheet, attributes: Array<AttributeDetailInfo>) => {
    attributes.forEach((attribute, index) => {
        const rowIndex = FIRST_DATA_ROW + index;
        const row = worksheet.getRow(rowIndex);

        const deprecated = capitalize(attribute.deprecated.toString());
        const required = capitalize(attribute.required.toString());
        const lastUsageDate = attribute.lastUsageDate ?
                formatDate(attribute.lastUsageDate, DEFAULT_DATE_FORMAT)
            : NO_ATTRIBUTE_USAGE;

        decorateTableCell(row, 1, setCellValue(attribute.name));
        decorateTableCell(row, 2, setCellValue(attribute.description ?? ''));
        decorateTableCell(row, 3, setCellValue(dataTypeString(attribute.fieldType, attribute.itemsType)));
        decorateTableCell(row, 4, setCellValue(getPossibleValuesOrDefault(attribute.possibleValues)));
        decorateTableCell(row, 5, setCellValue(required, HorizontalAlignment.CENTER));
        decorateTableCell(row, 6, setCellValue(deprecated, HorizontalAlignment.CENTER));
        decorateTableCell(row, 7, setCellValue(lastUsageDate, HorizontalAlignment.CENTER));
    });
};
